/**
 * Trade fill info popup
 *
 * Opens where you click a marker on the chart. The marker itself only says
 * direction, and which way it faces — that is all a 6x8 arrowhead can say
 * without competing with price. Everything else about the fill lives here:
 *
 *   LONG  19:42:07                      MNQ Diamonds
 *   2 @ 24,518.25
 *
 *   EXIT  19:51:44
 *   2 @ 24,532.75
 *   +14.50 pts                            +$58.00
 *   from 24,518.25 · 9m 37s
 *   MAE  ~6.25 pts                         -$12.50
 *
 * An exit says EXIT rather than LONG/SHORT, and carries no attribution chip.
 * Both follow from the same fact: an exit's side is the OPPOSITE of the
 * position it closed, so labelling a sell-to-close "SHORT" would name a trade
 * that was never taken — and a position the bot opened can be closed by hand,
 * so a bot name on the exit would be a claim nothing supports. The colour still
 * follows the fill side, so the chip matches the arrow you clicked.
 *
 * ## What it made
 *
 * The last two lines only appear on a fill that CLOSED something, which is what
 * makes them worth the space: an entry has no result yet, and a popup that
 * showed "0.00 pts" on one would be answering a question nobody asked with a
 * number that is not true. They are computed in {@link buildTradeMarkers} from
 * the FIFO lot match, not here — see {@link RealizedPnL} — because a blended
 * exit's result depends on which lots it took, and the popup is handed a marker
 * and nothing else.
 *
 * An exit whose entry predates the stored fill history has no entry price to
 * measure against. It says so in words rather than showing a number, because
 * the alternative — a 0.00 where the truth is "unknown" — reads as a scratch
 * trade, and a scratch is a real outcome that this is not.
 *
 * ## What it cost to get there
 *
 * The MAE line is the last one, and it is the only figure here that is NOT
 * exact. It is the worst the trade went against you before it closed, read off
 * chart BAR lows and highs — the finest resolution this path has — so the true
 * intrabar worst is at least as deep and may be deeper. The popup says so twice
 * over: a tilde in front of the number, which is visible without hovering
 * anything, and the reason spelled out on hover. A figure derived from bars
 * must never be dressed as a tick-exact one, because this is precisely the
 * number someone sizes a stop with.
 *
 * The same three-way distinction the P&L draws applies again, and matters more
 * here: `~0.00 pts` means the trade never once traded against you, which is a
 * strong claim; not being able to measure it is the word "unknown", never a
 * zero. Unknown is styled as prose — italic and grey, like the no-P&L line —
 * so the two can never be confused at a glance.
 *
 * The result also shows on a REVERSAL, which is classified an entry but closed
 * the opposite position on its way through. There it is prefixed with the
 * quantity it closed, so the number cannot be read as belonging to the new
 * position the same fill opened.
 *
 * Read-only, deliberately. There is nothing to label or correct — a fill
 * happened, this reports it. The diamond popup edits because a candidate is an
 * opinion; a fill is a fact.
 *
 * Seconds are shown, not just hours and minutes: several fills routinely land
 * inside one bar, and to minute precision they would all read the same time.
 *
 * Positioned against the chart container and flipped when it would run off an
 * edge, matching the diamond popup.
 */

import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import type { AdverseExcursion, TradeMarker } from '../../utils/tradeMarkers';
import './TradeMarkerInfoOverlay.css';

interface TradeMarkerInfoOverlayProps {
  marker: TradeMarker;
  /** Click position within the chart container. */
  x: number;
  y: number;
  /** Container size, so the popup can flip rather than overflow. */
  containerWidth: number;
  containerHeight: number;
  onClose: () => void;
}

const WIDTH = 186;
/** Head plus the fill line. The result lines are added to this when they show. */
const BASE_HEIGHT = 62;
const PNL_ROW_HEIGHT = 16;
const CONTEXT_ROW_HEIGHT = 14;
const MAE_ROW_HEIGHT = 15;

/** Enough decimals for a quarter-point tick, without padding out whole ticks. */
const priceText = (p: number): string =>
  p.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

/**
 * Signed points, at full precision.
 *
 * Four decimals rather than two because a blended exit's points are a
 * qty-weighted mean and land off the tick grid — 6.9375 on four lots. Rounding
 * that to 6.94 would stop it multiplying out to the dollar figure printed
 * beside it, and two numbers that do not reconcile invite the reader to
 * disbelieve both.
 *
 * The `=== 0` branch exists to normalise a NEGATIVE zero — `-0 === 0` is true,
 * so it resolves to a positive one. Without it a rounded-away loss would print
 * as "-0.00 pts", which reads as a loss too small to name rather than a scratch.
 */
const pointsText = (points: number): string => {
  const p = points === 0 ? 0 : points;
  const body = p.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  return `${p > 0 ? '+' : ''}${body} pts`;
};

/** Signed dollars, with the sign OUTSIDE the currency mark: -$34.00, not $-34. */
const dollarsText = (dollars: number): string => {
  const d = dollars === 0 ? 0 : dollars;
  const sign = d > 0 ? '+' : d < 0 ? '-' : '';
  const body = Math.abs(d).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${sign}$${body}`;
};

/**
 * The adverse excursion, as a magnitude with a tilde in front of it.
 *
 * No sign: {@link AdverseExcursion} is >= 0 by construction and the label "MAE"
 * already says which way it went, so a minus here would be decoration on the
 * points and is instead spent where it reads as money — on the dollars beside
 * it. Four decimals, matching {@link pointsText}, so the two columns line up.
 *
 * The tilde is the honest part and is never conditional. It marks the figure as
 * bar-derived, and it belongs on `0.00` most of all: "it never went against
 * you" is the strongest thing this line can say, and a bar low cannot actually
 * prove it — only that no bar CLOSED its low through the entry.
 */
const maePointsText = (points: number): string =>
  `~${points.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })} pts`;

/**
 * The hover text on the MAE line: what it measured, on how much, and why it is
 * approximate.
 *
 * Built rather than fixed because the quantity genuinely varies. On a blended
 * exit the figure is the worst SINGLE lot's drawdown, so its dollars cover that
 * lot alone while the P&L above covers the whole close — two different scopes
 * one line apart, and the only defence against misreading them is to say so.
 */
const maeTitle = (mae: AdverseExcursion, closedQty: number): string => {
  const scope =
    mae.qty === closedQty
      ? `all ${closedQty} contract${closedQty === 1 ? '' : 's'}`
      : `the worst single entry lot, ${mae.qty} of ${closedQty} contracts`;
  return (
    `Maximum adverse excursion: the furthest this trade went against you between ` +
    `entry and exit. Measured on ${scope}. ` +
    `Read off chart bar lows and highs rather than ticks, so the real worst is at ` +
    `least this deep and may be deeper.`
  );
};

/**
 * How long it was held, at one step of precision below whatever it is.
 *
 * A scalp is seconds and a swing is hours, and the same format cannot serve
 * both without printing either "0h 0m 43s" or "2h". Trailing zero units are
 * dropped, so a clean two minutes reads "2m" rather than "2m 0s".
 */
const heldText = (ms: number): string => {
  const totalSec = Math.max(0, Math.round(ms / 1000));
  if (totalSec < 60) return `${totalSec}s`;
  const totalMin = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (totalMin < 60) return sec === 0 ? `${totalMin}m` : `${totalMin}m ${sec}s`;
  const hours = Math.floor(totalMin / 60);
  const min = totalMin % 60;
  return min === 0 ? `${hours}h` : `${hours}h ${min}m`;
};

const TradeMarkerInfoOverlay: React.FC<TradeMarkerInfoOverlayProps> = ({
  marker,
  x,
  y,
  containerWidth,
  containerHeight,
  onClose,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);

  // Escape closes; clicking outside closes. Both bound while open only — same
  // as the diamond popup, including the deferred mousedown.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    const onDown = (e: MouseEvent): void => {
      // `as HTMLElement` rather than the `as Node` the diamond popup uses:
      // eslint's globals don't include Node, so that spelling trips no-undef.
      if (ref.current && !ref.current.contains(e.target as HTMLElement)) onClose();
    };
    document.addEventListener('keydown', onKey);
    // Defer: the click that opened this popup would otherwise close it.
    const t = setTimeout(() => document.addEventListener('mousedown', onDown), 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      clearTimeout(t);
    };
  }, [onClose]);

  const long = marker.side === 'buy';
  const isExit = marker.kind === 'exit';
  const realized = marker.realized;
  // Priced when at least one lot it closed had a known entry. `points === null`
  // is the unknown-entry case and is deliberately NOT the same as zero.
  const priced = realized !== null && realized.points !== null && realized.dollars !== null;

  // The heat it took to get there. Only shown alongside a priced result: on an
  // unpriced close the "entry unknown" line already covers both, and a second
  // "unknown" underneath it would spend a line saying the same thing twice.
  const mae = realized && priced ? realized.mae : null;

  // Grows with what is actually drawn, so the flip stays honest — a popup that
  // measured itself two lines short would tuck under the cursor near an edge.
  const estHeight =
    BASE_HEIGHT +
    (realized ? PNL_ROW_HEIGHT : 0) +
    (priced ? CONTEXT_ROW_HEIGHT + MAE_ROW_HEIGHT : 0);

  // Flip away from whichever edge we're near.
  const left = x + WIDTH + 16 > containerWidth ? Math.max(4, x - WIDTH - 12) : x + 12;
  const top = y + estHeight + 16 > containerHeight ? Math.max(4, y - estHeight - 8) : y + 8;

  const d = new Date(marker.timestamp);
  // Local time, seconds precision. The date rides along because the marker set
  // spans several sessions and "19:42:07" alone would be ambiguous.
  const when = `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString(
    [],
    { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }
  )}`;

  return (
    <div className="tmi-popup" style={{ left, top, width: WIDTH }} ref={ref}>
      <div className="tmi-head">
        {/* Coloured by the fill side either way, so this agrees with the arrow
            that was clicked; only the word changes. */}
        <span className={`tmi-side ${long ? 'long' : 'short'}`}>
          {isExit ? 'EXIT' : long ? 'LONG' : 'SHORT'}
        </span>
        <span className="tmi-when">{when}</span>
        <button className="tmi-close" onClick={onClose} title="Close">
          <X size={11} />
        </button>
      </div>

      <div className="tmi-line">
        <span className="tmi-fill">
          <span className="tmi-num">{marker.qty}</span>
          <span className="tmi-at">@</span>
          <span className="tmi-num">{priceText(marker.price)}</span>
        </span>
        {/* Same distinction the marker's rim draws, spelled out. Entries only:
            an omitted chip says "not known", which is the truth about who
            closed a position; a "manual" chip there would be an assertion. */}
        {!isExit && (
          <span className={`tmi-who ${marker.botName ? 'bot' : 'manual'}`}>
            {marker.botName ?? 'manual'}
          </span>
        )}
      </div>

      {/* Only on a fill that closed something. See the module note. MAE is
          named here too: with no entry price there is nothing to measure the
          bars against either, and leaving it unmentioned would invite the
          reading that the excursion was zero. */}
      {realized && !priced && (
        <div className="tmi-unknown" title="This close unwound a position opened before the stored fill history begins, so there is no entry price to measure it against — neither for P&L nor for the adverse excursion.">
          entry unknown — no P&amp;L or MAE
        </div>
      )}

      {realized && priced && (
        <>
          <div
            className={`tmi-pnl ${
              realized.dollars! > 0 ? 'win' : realized.dollars! < 0 ? 'loss' : 'flat'
            }`}
          >
            <span className="tmi-pts">{pointsText(realized.points!)}</span>
            <span className="tmi-usd">{dollarsText(realized.dollars!)}</span>
          </div>

          <div className="tmi-ctx">
            {/* A reversal is drawn as an ENTRY but closed the old position on
                its way through. Naming the quantity keeps the result from
                reading as the new position's. */}
            {!isExit && <>closed {realized.qty} </>}
            from <span className="tmi-ctx-num">{priceText(realized.entryPrice!)}</span>
            {realized.heldMs !== null && <> · {heldText(realized.heldMs)}</>}
            {/* Part of this close unwound the pre-history position. The number
                above covers the rest of it, and says so rather than implying
                it covered the lot. */}
            {realized.unknownQty > 0 && (
              <span className="tmi-partial" title="Contracts closed against a position that predates the stored fill history. They are excluded from the P&L above.">
                {' '}
                · {realized.unknownQty} unmatched
              </span>
            )}
          </div>

          {/* How much heat it took before it got here. Approximate, and says so
              in the number itself — see the module note. */}
          {mae ? (
            <div
              className={`tmi-mae ${mae.points > 0 ? 'heat' : 'none'}`}
              title={maeTitle(mae, realized.qty)}
            >
              <span className="tmi-mae-label">MAE</span>
              <span className="tmi-mae-pts">{maePointsText(mae.points)}</span>
              {/* Negated on the way in, so the shared formatter puts the sign
                  outside the currency mark and normalises the -0 case to a
                  plain $0.00 rather than "-$0.00". */}
              <span className="tmi-mae-usd">{dollarsText(-mae.dollars)}</span>
            </div>
          ) : (
            <div
              className="tmi-mae unknown"
              title="The bars this trade lived through are not loaded — either none are, or the entry is older than the oldest bar on the chart. Scroll back far enough to load them and this fills in. It is not zero: zero would mean the trade never went against you."
            >
              <span className="tmi-mae-label">MAE</span>
              <span className="tmi-mae-none">unknown — bars not loaded</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TradeMarkerInfoOverlay;
